import type { CommissionId } from '@sorrem/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { CutscenePlayer, type CutsceneOutcome } from '@sorrem-client/ui/features/Cutscene';
import { useAcceptCommission, useAppNavigate, useOfferCutscene } from '@sorrem-client/ui/hooks';
import { toCruciblePath } from '@sorrem-client/ui/features/CharacterHome/crucible-links';

export interface CommissionAcceptance {
    /** Accepting (or returning to a commission's run), or its offer cutscene still loading. */
    readonly isAccepting: boolean;
    /**
     * Accepts a commission, or returns to an accepted one's run, and enters
     * its crucible. When `offerCutsceneId` (its `Commission.offerCutscene`)
     * is still unseen, it plays first: the meeting comes before the yes.
     */
    readonly accept: (commissionId: CommissionId, offerCutsceneId?: string) => Promise<void>;
}

const CommissionAcceptanceContext = createContext<CommissionAcceptance | null>(null);

interface CommissionOfferProviderProps {
    /** The Overview tab shows: a pending offer cutscene plays by itself there, once (game-intro.md). */
    readonly isOverviewShown: boolean;
    readonly children: ReactNode;
}

/** An offer cutscene playing, and the commission waiting on it to be accepted, if any. */
interface PlayingOffer {
    readonly cutsceneId: string;
    readonly thenAccept: CommissionId | null;
}

/**
 * Character Home's one owner of commission offers (#330): it plays the
 * clerk's offer cutscene, by itself on the Overview tab or before a
 * commission is accepted from anywhere on the page, and accepts. Every
 * tab's panels stay mounted, so a player rendered by a panel showed (and
 * stacked) on tabs that weren't open.
 */
export function CommissionOfferProvider({ isOverviewShown, children }: CommissionOfferProviderProps): JSX.Element {
    const navigate = useAppNavigate();
    const { isAccepting, accept } = useAcceptCommission();
    const offerCutscene = useOfferCutscene();
    const { markSeen } = offerCutscene;
    const [playing, setPlaying] = useState<PlayingOffer | null>(null);
    // The last offer cutscene played here, so it isn't shown again before the server records it seen.
    const [finishedCutsceneId, setFinishedCutsceneId] = useState<string | null>(null);
    const pendingCutsceneId = offerCutscene.cutsceneId !== finishedCutsceneId ? offerCutscene.cutsceneId : null;

    // Accepting creates (or reopens) the commission's crucible: go straight to its gathering page.
    const acceptAndEnter = useCallback(async (commissionId: CommissionId): Promise<void> => {
        const crucibleId = await accept(commissionId);
        if (crucibleId !== null) {
            navigate(toCruciblePath(crucibleId));
        }
    }, [accept, navigate]);

    const acceptCommission = useCallback(async (commissionId: CommissionId, offerCutsceneId?: string): Promise<void> => {
        if (offerCutsceneId !== undefined && offerCutsceneId === pendingCutsceneId) {
            setPlaying({ cutsceneId: offerCutsceneId, thenAccept: commissionId });
            return;
        }
        await acceptAndEnter(commissionId);
    }, [acceptAndEnter, pendingCutsceneId]);

    // By itself only on the Overview, and once started it plays to the end: the
    // cutscene covers the screen, which hides the Overview, so deriving it
    // from `isOverviewShown` would unmount and remount it forever.
    useEffect(() => {
        if (isOverviewShown && pendingCutsceneId !== null && playing === null) {
            setPlaying({ cutsceneId: pendingCutsceneId, thenAccept: null });
        }
    }, [isOverviewShown, pendingCutsceneId, playing]);

    const handleFinished = useCallback((outcome: CutsceneOutcome): void => {
        const finished = playing;
        setPlaying(null);
        if (finished === null) {
            return;
        }
        setFinishedCutsceneId(finished.cutsceneId);
        if (outcome !== 'failed') {
            void markSeen(finished.cutsceneId);
        }
        if (finished.thenAccept !== null) {
            void acceptAndEnter(finished.thenAccept);
        }
    }, [acceptAndEnter, markSeen, playing]);

    const acceptance = useMemo<CommissionAcceptance>(() => ({
        isAccepting: isAccepting || offerCutscene.isLoading,
        accept: acceptCommission,
    }), [acceptCommission, isAccepting, offerCutscene.isLoading]);

    return (
        <CommissionAcceptanceContext.Provider value={acceptance}>
            {children}
            {playing !== null && <CutscenePlayer cutsceneId={playing.cutsceneId} onFinished={handleFinished} />}
        </CommissionAcceptanceContext.Provider>
    );
}

/** Accepting commissions on Character Home, inside `CommissionOfferProvider`. */
export function useCommissionAcceptance(): CommissionAcceptance {
    const acceptance = useContext(CommissionAcceptanceContext);
    if (acceptance === null) {
        throw new Error('useCommissionAcceptance must be used within a CommissionOfferProvider');
    }
    return acceptance;
}
